/**
 * WRITING DIRECTION for a React Native subtree — the one place in this repo that knows how each
 * platform spells it.
 *
 * Mount `DirectionRoot` once per host (the kiosk does it in `kiosk/KioskApp`), and every
 * descendant lays out mirrored when it says `rtl`. Nothing below needs to know: `flexDirection:
 * "row"`, the logical edges (`marginStart`, `paddingEnd`, `start`/`end`, `borderStartWidth`,
 * the logical corner radii) and `textAlign: "start"/"end"` all flip on their own, identically on
 * both platforms. PHYSICAL edges (`marginLeft`, `left`, `textAlign: "left"`) flip on NEITHER,
 * which is why they are banned inside `packages/ui/kiosk` by `pnpm audit:uplift` check M.
 *
 * ## How each platform gets there — they are not the same mechanism
 *
 *   native  Yoga lays the subtree out RTL and resolves `Edge::Start`/`Edge::End` for real.
 *   web     react-native-web does NOT emit CSS logical properties. It POLYFILLS them: one
 *           `marginStart` compiles to two atomic classes, `margin-left` and `margin-right`, and
 *           the writing direction decides which is on the element (`StyleSheet/compiler`,
 *           `PROPERTIES_I18N` / `PROPERTIES_FLIP`).
 *
 * The outcome is identical, which is what makes one style tree serve both — but the difference
 * matters the moment anyone debugs it. ⚠️ **On web the class name lies**: react-native-web names
 * the identifier after the SOURCE property and caches by the RESOLVED one, so a plain
 * `marginLeft: 12` is handed the cached class `r-marginInlineStart-…`. Two elements can carry
 * the same logical-looking class and only one of them flips. Read the RULE, not the name —
 * `./rendercheck.tsx` does, and it is the gate that proves any of this works.
 *
 * 🔴 **`I18nManager` is deliberately not used, and must not be added.** Three reasons, each
 * sufficient on its own:
 *
 *   1. On web it does nothing at all. `react-native-web`'s `I18nManager` is a no-op stub —
 *      `forceRTL() { return; }`, and `getConstants()` returns a hardcoded `{isRTL: false}`.
 *   2. On native it needs an app RESTART. A customer choosing a language mid-order cannot
 *      restart the terminal.
 *   3. It would manufacture a platform divergence. `forceRTL` switches on Yoga's
 *      `swapLeftAndRightInRTL`, which flips PHYSICAL props (`marginLeft`, `left`) on native
 *      only — web never flips them. That is the "ships looking perfect on one platform" class
 *      of bug this repo has been bitten by twice (host CSS, Yoga errata).
 *
 * The per-subtree direction below has none of those: it is runtime, needs no restart, and leaves
 * physical props physical on BOTH platforms — so a `marginLeft` is equally wrong everywhere
 * rather than wrong in one place, and `pnpm audit:uplift` check M catches it.
 */
import React, { createContext, useContext } from "react";
import { Platform, StyleSheet, View, type StyleProp, type TextStyle, type ViewProps } from "react-native";
// Pure, and that matters: this file is imported by every host root. `@rista/core/utils/i18n`
// pulls in no store, no navigation and no platform API — unlike `utils/currency`, whose module
// root drags the store graph in and which `kiosk/theme` therefore has to import by `/format`.
import { DEFAULT_DIRECTION, type Direction } from "@rista/core/utils/i18n";

const DirectionContext = createContext<Direction>(DEFAULT_DIRECTION);

/** The active writing direction. `ltr` where no `DirectionRoot` is mounted — never throws. */
export function useDirection(): Direction {
  return useContext(DirectionContext);
}

/**
 * For the few places that must branch in JS rather than in a style: a chevron that has to point
 * the other way, a horizontal carousel's offset arithmetic, a scroll position.
 *
 * ⚠️ **Not for layout.** Reaching for this to pick between two stylesheets is the mistake — the
 * logical properties already flip, and a hand-branched layout is one someone has to keep in sync
 * with itself forever.
 */
export function useIsRTL(): boolean {
  return useContext(DirectionContext) === "rtl";
}

/**
 * WEB ONLY: the side a line of text starts on, from the LAYOUT. react-native-web marks a root `Text` and every
 * `TextInput` `dir="auto"`, which is right for the words' own order (an English "Kulfi Roll (650g)" must not
 * reorder in an Arabic screen), and gives every `Text` its own `text-align: start`, so nothing is inherited and
 * `start` follows the WORDS: English data, a category name that wraps, sat on the left of a right-to-left screen
 * (found 2026-10-09). A text member applies this itself; inheriting it from `DirectionRoot` cannot work. This turns `start` (the default) or `end` into the layout's physical side for an RTL
 * subtree on web. Native needs nothing: Android aligns by the paragraph's direction, which `DirectionRoot` sets.
 * `null` everywhere else, so a left-to-right screen is untouched.
 */
export function useWebTextAlign(align: "start" | "end" = "start"): { textAlign: "left" | "right" } | null {
  const rtl = useIsRTL();
  if (Platform.OS !== "web" || !rtl) return null;
  return align === "start" ? RIGHT : LEFT;
}

/**
 * For a text component (`Text`, `KioskText`): the layout's side when the caller's style leaves the alignment to
 * the layout (none, `auto`, `start`, `end`) in a right-to-left screen on web; `null` for `center` and `justify`,
 * on native, and left to right. Put it LAST in the style array. Reads the caller's style only in an RTL screen.
 */
export function useLayoutTextAlign(style: StyleProp<TextStyle>): { textAlign: "left" | "right" } | null {
  // a string: apps/web types React Native at 0.70, whose textAlign has no "start" or "end"
  const asked: string | undefined = useIsRTL() && style ? StyleSheet.flatten(style)?.textAlign : undefined;
  const side = useWebTextAlign(asked === "end" ? "end" : "start");
  return asked === undefined || asked === "auto" || asked === "start" || asked === "end" ? side : null;
}

const RIGHT = { textAlign: "right" as const };
const LEFT = { textAlign: "left" as const };

export interface DirectionRootProps extends ViewProps {
  direction: Direction;
}

/**
 * The host's root `View`, carrying the direction and providing it to everything below.
 *
 * It is one component rather than a provider plus a style helper because the two must be applied
 * at the same node to agree, and a pair of exports used together is a pair someone eventually
 * uses apart. Takes a `direction`, not a language: a rendercheck or the language picker's own
 * preview wants to force one without owning a locale. The host derives it once — the kiosk with
 * `directionFor()` in `@rista/core/kiosk/language`.
 *
 * ⚠️ **The two platforms spell this differently and neither type system knows the other's
 * spelling**, which is the whole reason this file exists:
 *
 *   web     the `dir` ATTRIBUTE. `react-native-web`'s `View` forwards it to the DOM and feeds
 *           its own locale context from it, so nested `Text` inherits too. It is NOT on React
 *           Native's `ViewProps`, so it is cast here. A `direction` STYLE does not work on web
 *           at all — RNW's style validator rejects the key and deletes it in development.
 *   native  the `direction` STYLE, which React Native hands to Yoga as the node's layout
 *           direction. `dir` is meaningless there.
 */
export function DirectionRoot({ direction, style, ...rest }: DirectionRootProps) {
  const webDir = Platform.OS === "web" ? ({ dir: direction } as object) : null;
  const nativeDirection = Platform.OS === "web" ? null : { direction };
  return (
    <DirectionContext.Provider value={direction}>
      <View {...rest} {...webDir} style={[style, nativeDirection]} />
    </DirectionContext.Provider>
  );
}

export type { Direction };
export default DirectionRoot;
